/**
 * ITER297 · Part 4 — Manager-shell Compliance Intelligence.
 *
 * Consumes the same /api/ops/compliance-trends endpoint which auto-scopes
 * to the caller's assigned_outlet_ids when they aren't admin/area_manager.
 * We give managers the trend chart + a lightweight resolution / SLA / repeat
 * strip — no cross-outlet portfolio widgets.
 */
import React, { useEffect, useState, useCallback, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  RefreshCw, TrendingUp, Clock, AlertTriangle,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { SectionBand, SectionTitle } from '../components/ops/SectionBand';
import { useAuth } from '../contexts/AuthContext';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

const API = process.env.REACT_APP_BACKEND_URL;

const TREND_PALETTE = ['var(--color-primary)', '#C9A34E', '#B45309', '#7C3AED', '#0F766E',
                       '#B91C1C', '#1D4ED8', '#334155'];

export default function ManagerComplianceIntelligence() {
  const { user, getHeaders } = useAuth();
  const [period, setPeriod] = useState('daily');
  const [groupBy, setGroupBy] = useState('department');
  const [rows, setRows] = useState([]);
  const [buckets, setBuckets] = useState([]);
  const [failures, setFailures] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [tr, top] = await Promise.all([
        axios.get(`${API}/api/ops/compliance-trends?period=${period}&group_by=${groupBy}`,
                    { headers: getHeaders() }),
        axios.get(`${API}/api/ops/top-recurring-failures?limit=5&days=30`,
                    { headers: getHeaders() }),
      ]);
      setRows(tr.data?.rows || []);
      setBuckets(tr.data?.buckets || []);
      setFailures(top.data?.rows || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load intelligence');
    } finally { setLoading(false); }
  }, [period, groupBy, getHeaders]);
  useEffect(() => { load(); }, [load]);

  const chartData = useMemo(() => {
    const map = new Map();
    buckets.forEach((b) => map.set(b, { bucket: b }));
    rows.forEach((r) => {
      const row = map.get(r.bucket) || { bucket: r.bucket };
      row[r.group_label] = r.compliance_pct;
      map.set(r.bucket, row);
    });
    return Array.from(map.values());
  }, [rows, buckets]);
  const groups = useMemo(() => {
    const set = new Set();
    rows.forEach((r) => set.add(r.group_label));
    return Array.from(set).slice(0, 8);
  }, [rows]);

  return (
    <div className="min-h-screen bg-white" data-testid="manager-compliance-intel">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
        <SectionBand
          title="Compliance Intelligence"
          subtitle={`Scope: your assigned outlets · role: ${user?.role || ''}`}
          icon={TrendingUp}
          testId="mgr-intel-page-band"
          actions={
            <Button variant="ghost" size="sm" onClick={load}
                     className="text-white/90 hover:text-white hover:bg-white/10">
              <RefreshCw className={`w-3.5 h-3.5 mr-1 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          }
        />

        <SectionTitle
          title="Compliance Trends"
          subtitle={`${period} · by ${groupBy} · top ${groups.length || 0} shown`}
          icon={TrendingUp}
          testId="mgr-band-trends"
          actions={
            <div className="flex items-center gap-2">
              <Select value={period} onValueChange={setPeriod}>
                <SelectTrigger className="h-7 w-24 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="daily">Daily</SelectItem>
                  <SelectItem value="weekly">Weekly</SelectItem>
                  <SelectItem value="monthly">Monthly</SelectItem>
                </SelectContent>
              </Select>
              <Select value={groupBy} onValueChange={setGroupBy}>
                <SelectTrigger className="h-7 w-32 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="department">By Department</SelectItem>
                  <SelectItem value="outlet">By Outlet</SelectItem>
                </SelectContent>
              </Select>
            </div>
          }
        />
        <div className="bg-white rounded-lg border border-[#E2E8DF] p-4 mb-5"
             data-testid="compliance-trend-chart" style={{ height: 320 }}>
          {loading ? (
            <div className="h-full flex items-center justify-center text-[#5C6B64]">Loading…</div>
          ) : chartData.length === 0 ? (
            <div className="h-full flex items-center justify-center text-[#5C6B64]">No data yet.</div>
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData} margin={{ top: 8, right: 16, bottom: 8, left: -8 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8DF" />
                <XAxis dataKey="bucket" tick={{ fontSize: 10, fill: '#5C6B64' }} />
                <YAxis domain={[0, 100]} tick={{ fontSize: 10, fill: '#5C6B64' }}
                       tickFormatter={(v) => `${v}%`} />
                <Tooltip contentStyle={{ fontSize: 11, border: '1px solid #E2E8DF' }}
                          formatter={(v) => `${v}%`} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                {groups.map((g, i) => (
                  <Line key={g} type="monotone" dataKey={g}
                        stroke={TREND_PALETTE[i % TREND_PALETTE.length]}
                        strokeWidth={2} dot={{ r: 3 }} activeDot={{ r: 5 }} connectNulls />
                ))}
              </LineChart>
            </ResponsiveContainer>
          )}
        </div>

        <SectionTitle title="Top Recurring Failures" icon={AlertTriangle}
                     testId="mgr-band-recurring" />
        <div className="bg-white rounded-lg border border-[#E2E8DF] p-4"
             data-testid="mgr-recurring-widget">
          {failures.length === 0 ? (
            <div className="text-xs text-[#5C6B64] italic py-6 text-center">
              No recurring failures yet — keep it up.
            </div>
          ) : (
            <div className="divide-y divide-[#E2E8DF]">
              {failures.map((r, i) => (
                <div key={i} className="py-2 flex items-center justify-between text-xs">
                  <div className="flex items-center gap-3">
                    <div className="font-mono text-[#5C6B64] w-6">#{i + 1}</div>
                    <div>
                      <div className="text-sm text-[#1A2421] font-medium">{r.task_name}</div>
                      <div className="text-[10px] text-[#5C6B64]">
                        {r.department} · {r.outlets_affected} outlet{r.outlets_affected !== 1 ? 's' : ''}
                      </div>
                    </div>
                  </div>
                  <div className="text-lg font-semibold text-[#C2410C]">
                    {r.failure_count}<span className="text-xs">×</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
